import React, { useEffect, useState, useContext } from 'react';
import {
	Grid,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardEnvContext from "../../../../DashboardApp/DashboardEnvContext";
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { SSO_WIDGET_URL } from './constants';
import AuthErrorModal from './authErrorModal';


const SSO_PAYLOAD = {
	accessToken: '',
	refreshToken: '',
	userId: '',
	loyaltyId: '',
	updateForm: ''
};

const BulkPurchaseAuth = ({ isVisible, onClose, onSubmit, userEmail, loadingHandler, onLoggedOut }) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	const dashboard = useContext(DashboardEnvContext);

	const [isScriptAppended, setIsScriptAppended] = useState(false);
	const [isInit, setIsInit] = useState(false);
	const [ssoPayload, setSsoPayload] = useState({ ...SSO_PAYLOAD });
	const [ssoVerified, setSsoVerified] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// error modal
	const [isError, setIsError] = useState(false);
	const [errMsg, setErrMsg] = useState('');

	const handleCloseModal = () => {
		onClose(false);
	};

	useEffect(() => {
		if (!isScriptAppended) {
			const script = document.createElement('script');
			script.src = SSO_WIDGET_URL;
			script.async = true;
			document.body.appendChild(script);
			script.onload = () => setIsScriptAppended(true);
			return () => {
				document.body.removeChild(script);
			};
		}
  }, [isScriptAppended]);

	const resetSsoPayload = () => setSsoPayload({ ...SSO_PAYLOAD });
	
	const logoutSso = () => {
		window.aaWidget.logout().then(res => {
			resetSsoPayload();
			setIsInit(false);
			setSsoVerified(false);
			onLoggedOut();
		}).catch(err => {
			dashboard.utils.showErrorNotification(`SSO Logout Error: ${err.message}`);
		});
	}
	
	useEffect(() => {
		if (ssoPayload && ssoPayload.accessToken && userEmail) {
			window.aaWidget.getUser().then(res => {
				if (res.username !== userEmail) {
					promptError(`User SSO username(${res.username}) is different from logged in email(${userEmail}) in dashboard.`);
					return	
				}
				setSsoVerified(true);
			}).catch(err => {
				console.error("get user error:", err);
			});
		}
	}, [ssoPayload, userEmail])

	const ssoInitFailHandler = (res) => {
		setIsLoading(false);
		if (res === 'SESSION_ERROR') return;

		dashboard.utils.showErrorNotification(`SSO ERROR: ${res}`);
	};

	const ssoInitSuccessHandler = (res) => {
		setIsLoading(true);
		setSsoPayload(res);
	};

	const promptError = (message) => {
		setErrMsg(message);
		setIsError(true);
	}
	const errorModalCancelHandler = () => {
		handleCloseModal();
	}
	const errorModalTryAgainHandler = () => {
		logoutSso();
	}

	useEffect(() => {
		if (isVisible && !isInit && isScriptAppended) {
			window.aaWidget.initialize({
				elementRoot: 'airasia_widget',
				hideSignupTab: true,
				successCallback: ssoInitSuccessHandler,
				failureCallback: ssoInitFailHandler,
				orientation: 'horizontal',
				lang: 'en-GB'
			}).then(res => {
				setIsInit(true);
				setIsLoading(false);
				return window.aaWidget.isLoggedIn();
			}).catch(err => {
				dashboard.utils.showErrorNotification(`Error Initialize SSO Widget: ${err.message}`);
				setIsInit(false);
			});
		}
	}, [isInit, isVisible, isScriptAppended]);

	useEffect(() => {
		if (ssoVerified && ssoPayload && ssoPayload.accessToken) {
			loadingHandler(true);
			onSubmit(ssoPayload.accessToken);
			onClose();
		}
	}, [ssoVerified])

	return (
		<Dialog
			open={isVisible}
			onClose={handleCloseModal}
			fullScreen={fullScreen}
			scroll="paper"
			maxWidth={'sm'}
		>
			<DialogContent dividers>
				<Box sx={{ flexGrow: 1 }}>
					<Grid container spacing={3} columns={12} direction="column">
						<Grid item md={7} xs={12}>
							<Box sx={{ flexGrow: 1 }}>
								<Grid container spacing={3}>
									<Grid container item md={12} xs={12}>
										<div id="airasia_widget"></div>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCloseModal}>Cancel</Button>
			</DialogActions>

			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
				open={isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			<AuthErrorModal 
				isVisible={isError}
				onCancel={errorModalCancelHandler}
				onTryAgain={errorModalTryAgainHandler}
				message={errMsg}
			/>
		</Dialog>
	);
};

export default BulkPurchaseAuth;
